import { Injectable }   from "@angular/core";
import { environment }  from "src/environments/environment";
import { InfinityUtil, httpPromise } from 'src/infinity-ui';    // { get, set }
import { ModelService } from './model.service';
import { ErrorService } from './error.service';
const { apiBase, production } = environment;
const TOKEN_EXCHANGE_GRANT = "urn:ietf:params:oauth:grant-type:token-exchange";
const TOKEN_EXCHANGE_RESOURCE = "app-ui-submission";
const TOKEN_EXCHANGE_URL = `${environment.apiBase}/oauth/token`;
const CAPABILITY_ACCESS_TOKEN_KEY = "arc_capability_access_token";
const CAPABILITY_EXPIRES_AT_KEY = "arc_capability_expires_at";
const INTAKE_UPDATE_CAPABILITY_TOKEN_KEY = "arc_intake_update_capability_token";
const INTAKE_UPDATE_CAPABILITY_RATING_ID_KEY = "arc_intake_update_capability_rating_id";
const INTAKE_UPDATE_CAPABILITY_EXPIRES_AT_KEY = "arc_intake_update_capability_expires_at";
const CAPABILITY_CONTEXT_PARTICIPANT_RATING_ID_PATH = "capabilityContext.participantRatingId";
const CAPABILITY_CONTEXT_OPERATION_PATH = "capabilityContext.operation";
const HTTP_POST_METHOD = "POST";
const URL_ENCODED_FORM_CONTENT_TYPE = "application/x-www-form-urlencoded;charset=UTF-8";

function createRequestId(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }

    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, value => value.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}


@Injectable({ providedIn: "root" })
export class ApiService {

    constructor(public util: InfinityUtil, public model: ModelService, public errors: ErrorService) { }

    /* ----- Root ----- */

    async getProgramOpen(): Promise<any> {
        const programName = this.util.get("programName");
        const url = `/api/intake/ping/${programName}`;
        return this.api("programOpen", "get", url);
    }

    async getOpsApiStatus(): Promise<any> {
        const programName = this.util.get("programName");
        return this.api("opsApiStatus", "get", `/api/system/status/${programName}`);
    }

    async getApplicationForm(): Promise<any> {
        const { programName, renewalToken } = this.util.get(["programName", "renewalToken"]);
        const response = await this.api("applicationForm", "get", `/api/intake/${renewalToken || programName}`);
        const programId = response?.programEnrollment?.programId ||
            this.util.get("applicationFormResponse.programEnrollment.programId");
        if (programId) {
            await this.loadProgramRollout(programId);
        }
        return response;
    }

    async getParticipantRatingSummary(): Promise<any> {
        const participantToken = this.util.get("participantToken");
        return this.api("participantRatingSummary", "get", `/api/intake/participantratingsummary/${participantToken}`);
    }

    /* ----- Landing ----- */

    async postVerifyEmail(): Promise<any> {
        return this.api("verifyEmail", "post", "/api/intake/verifyEmail");
    }

    async issueCapabilityForCurrentApplicant(): Promise<any> {
        const program = this.util.get("programName");
        const email = this.util.get("verifyEmail.value") || this.util.get("email.value");
        if (!program || !email) {
            return null;
        }
        const clientCorrelationId = `web-intake-${createRequestId()}`;
        const response = await httpPromise("POST", environment.apiBase + "/api/v1/capabilities/tokens", {
            data: { email, program, clientCorrelationId }
        });
        if (response?.token && !response.expiresAt && Number.isFinite(Number(response.expires_in))) {
            response.expiresAt = Date.now() + Number(response.expires_in) * 1000;
        }
        if (response?.token) {
            sessionStorage.setItem("arc_capability_program", String(program).trim().toLowerCase());
            sessionStorage.setItem("arc_capability_email", String(email).trim().toLowerCase());
            this.storeCapability(response.token, response.expiresAt, true);
        }
        return response;
    }

    getCapabilityExpiresAt(): number | null {
        const storedExpiry = Number(sessionStorage.getItem(CAPABILITY_EXPIRES_AT_KEY));
        return Number.isFinite(storedExpiry) && storedExpiry > 0 ? storedExpiry : null;
    }

    hasCapabilityToken(): boolean {
        return !!sessionStorage.getItem(CAPABILITY_ACCESS_TOKEN_KEY);
    }

    isCapabilityExpiredOrExpiryUnknown(): boolean {
        if (!this.hasCapabilityToken()) { return false; }
        const expiresAt = this.getCapabilityExpiresAt();
        // Treat tokens cached before expiry tracking was added as stale so the
        // next Pay attempt replaces them instead of trusting unknown lifetime.
        return expiresAt === null || expiresAt <= Date.now();
    }

    isCapabilityExpiryKnown(): boolean {
        return this.getCapabilityExpiresAt() !== null;
    }

    async renewCapabilityForCurrentApplicant(): Promise<boolean> {
        try {
            const response = await this.issueCapabilityForCurrentApplicant();
            if (!response?.token) { return false; }
            this.clearIntakeUpdateCapability();
            return !!response?.token;
        } catch {
            return false;
        }
    }

    async requestRatingBoundIntakeRecovery(email: string): Promise<boolean> {
        const ratingId = Number(
            this.util.get(CAPABILITY_CONTEXT_PARTICIPANT_RATING_ID_PATH) ||
            this.util.get("participantRatingId") ||
            this.util.get("participantRating.participantRatingId") || 0);
        const normalizedEmail = String(email || "").trim();
        if (!Number.isSafeInteger(ratingId) || ratingId <= 0 || !normalizedEmail) {
            return false;
        }

        await httpPromise("POST", environment.apiBase + "/api/v2/capabilities/intake/recovery", {
            data: { participantRatingId: ratingId, email: normalizedEmail },
            rejectHttpErrors: true
        });
        return true;
    }

    clearCurrentApplicantCapability(): void {
        sessionStorage.removeItem(CAPABILITY_ACCESS_TOKEN_KEY);
        sessionStorage.removeItem(CAPABILITY_EXPIRES_AT_KEY);
        sessionStorage.removeItem("arc_capability_email");
        sessionStorage.removeItem("arc_capability_program");
        this.clearIntakeUpdateCapability();
    }

    private storeCapability(token: string, expiresAt: string | number, monitorExpiry = false): void {
        sessionStorage.setItem(CAPABILITY_ACCESS_TOKEN_KEY, token);
        const expiry = typeof expiresAt === "number" ? expiresAt : Date.parse(expiresAt);
        if (Number.isFinite(expiry) && expiry > 0) {
            sessionStorage.setItem(CAPABILITY_EXPIRES_AT_KEY, String(expiry));
        } else {
            sessionStorage.removeItem(CAPABILITY_EXPIRES_AT_KEY);
        }
        if (!monitorExpiry) {
            sessionStorage.removeItem("arc_capability_email");
            sessionStorage.removeItem("arc_capability_program");
        } else {
            window.dispatchEvent(new Event("arc-capability-token-updated"));
        }
    }

    /* ----- Register ----- */

    async postFormValidation(): Promise<any> {
        if (this.usesV2WebIntake()) {
            return this.postV2Preview();
        }
        return this.api("formValidation", "post", "/api/intake");
    }

    private usesV2WebIntake(): boolean {
        const rollout = this.util.get("programRollout");
        // ARC is authoritative for registered-program routing. V2 is the
        // fail-safe when rollout metadata cannot be loaded; callers must not
        // silently fall back to the legacy proxy because of a missing flag.
        return !rollout || String(rollout.intakeMode || "V2").toUpperCase() === "V2";
    }

    public isV2WebIntakeForUi(): boolean {
        return this.usesV2WebIntake();
    }

    public getPaymentProviderForUi(): string {
        const rollout = this.util.get("programRollout");
        const provider = this.normalizePaymentProvider(rollout?.paymentProvider || rollout?.PaymentProvider);
        if (provider === "APPLIEDPAY") { return "AppliedPay"; }
        if (provider === "NONE") { return "None"; }
        // Card Connect is the safe default when rollout metadata is missing or
        // contains an unrecognized provider. Applied Pay must be explicitly
        // selected by the backend rollout response.
        return "CardConnect";
    }

    public isAppliedPayProviderActiveForUi(): boolean {
        return this.getPaymentProviderForUi() === "AppliedPay";
    }

    public isAppliedPayProgramForUi(): boolean {
        return this.getPaymentProviderForUi() === "AppliedPay";
    }

    private normalizePaymentProvider(provider: any): string {
        return String(provider || "").replace(/[^a-z0-9]/gi, "").toUpperCase();
    }

    private async loadProgramRollout(programId: number): Promise<void> {
        try {
            const rollout = await httpPromise("GET", `${environment.apiBase}/api/v2/programs/${encodeURIComponent(String(programId))}/rollout`);
            if (rollout) {
                this.setStoreValue("programRollout", rollout);
            }
        } catch {
            // V2 remains the safe client fallback when rollout metadata is
            // temporarily unavailable. The ARC endpoint remains authoritative
            // whenever it can be reached.
            this.setStoreValue("programRollout", { intakeMode: "V2", paymentProvider: "CardConnect" });
        }
    }

    private setStoreValue(key: string, value: any): void {
        if (typeof this.util?.set === "function") {
            this.util.set({ [key]: value });
            return;
        }

        if (this.util?.store) {
            this.util.store[key] = value;
        }
    }

    private requestIds(prefix: string): any {
        const idempotencyKey = `${prefix}-${createRequestId()}`;
        return { idempotencyKey, clientCorrelationId: `${prefix}-${idempotencyKey}` };
    }

    private async postV2Preview(): Promise<any> {
        await this.ensureCapability();
        const enrollmentRequest = this.util.get(this.model.formValidationRequest);
        // V2 authorization requires the enrollment resource itself. Keep a
        // defensive fallback to the authoritative application-form payload;
        // a missing projection otherwise reaches ARC as enrollment id 0 and
        // is reported as "Program enrollment scope is required.".
        if (!enrollmentRequest?.programEnrollmentId) {
            const applicationEnrollment = this.util.get("applicationFormResponse.programEnrollment.programEnrollmentId");
            if (applicationEnrollment) {
                enrollmentRequest.programEnrollmentId = applicationEnrollment;
            }
        }
        if (!enrollmentRequest?.programEnrollmentId) {
            await this.getApplicationForm();
            const applicationEnrollment = this.util.get("applicationFormResponse.programEnrollment.programEnrollmentId");
            if (applicationEnrollment) {
                enrollmentRequest.programEnrollmentId = applicationEnrollment;
            }
        }
        if (!enrollmentRequest?.programEnrollmentId) {
            throw new Error("Program enrollment is required before rate preview.");
        }
        let response: any;
        try {
            response = await httpPromise("POST", environment.apiBase + "/api/v2/participants/ratings/preview", {
                data: { enrollmentRequest },
                headers: { "X-Capability-Token": this.getRatePreviewCapabilityToken() },
                rejectHttpErrors: true
            });
        } catch (error: any) {
            this.handleCapabilityRevoked(error);
            throw error;
        }
        const normalized = {
            ...response,
            isSuccessful: true,
            participantRatingId: response?.participantRatingId,
            itemSummaries: this.buildSelectedCoverageSummaries(enrollmentRequest),
            referralResponse: { isReferral: response?.isReferral === true },
            adjustedTaxes: response?.taxes,
            adjustedFees: response?.fee
        };
        // `api()` normally runs responseHandler(), which both stores the
        // response and projects formValidationProps into the root model. The
        // direct V2 call bypassed that projection, so the payment page saw a
        // successful response but had no premium/total/payment amount state.
        this.util.set({ formValidationResponse: normalized }, { batch: true });
        this.util.set(this.util.get(this.model.formValidationProps), { batch: true });
        return normalized;
    }

    private buildSelectedCoverageSummaries(enrollmentRequest: any): any[] {
        const selectedItems = enrollmentRequest?.programEnrollmentRatingItems ||
            this.util.get("selectedCoverages") ||
            [];
        if (!Array.isArray(selectedItems)) {
            return [];
        }

        return selectedItems
            .filter(item => item)
            .map(item => this.normalizeSelectedCoverageSummary(item))
            .filter(summary => summary.visible &&
                (summary.ratingItemDescription || summary.value || summary.parentRatingItemDescription || summary.ratingGroupDescription));
    }

    private normalizeSelectedCoverageSummary(item: any): any {
        const rawDisplay = item.ratingItemDescription || item.display || item.label || "";
        const tableCells = this.extractSummaryTableCells(rawDisplay);
        const semanticItemId = item.semanticItemId || item.itemId || "";
        const parentItemId = item.parentItemId || item.parentSemanticItemId || "";
        const inferredCoverageLabel = this.inferCoverageSummaryLabel(parentItemId || semanticItemId);
        const renderedParentLabel = this.getRenderedItemLabel(parentItemId);
        const inferredCoverageValue = this.inferCoverageSummaryValue(semanticItemId, tableCells, rawDisplay);
        const ratingGroupDescription = item.ratingGroupDescription ||
            item.groupDisplay ||
            item.groupDescription ||
            "";
        const parentRatingItemDescription = inferredCoverageLabel ||
            renderedParentLabel ||
            item.parentRatingItemDescription ||
            item.parentDisplay ||
            "";
        const useCoverageSummaryLabel = !!inferredCoverageLabel || tableCells.length > 0;
        const ratingItemDescription = useCoverageSummaryLabel
            ? parentRatingItemDescription || ratingGroupDescription || semanticItemId
            : rawDisplay || semanticItemId;
        const hasKnownCoverageValue = /_(EL|UM|EP(LI)?|PR)_(?:\d+M|\d+K|Decline|Yes|No)$/i.test(semanticItemId);
        let value: any;
        if (hasKnownCoverageValue) {
            value = inferredCoverageValue || item.value || tableCells.join(" ");
        } else if (item.value !== null && item.value !== undefined) {
            value = item.value;
        } else if (inferredCoverageValue !== null && inferredCoverageValue !== undefined) {
            value = inferredCoverageValue;
        } else if (tableCells.length > 0) {
            value = tableCells.join(" ");
        } else {
            value = item.value;
        }

        return {
            ...item,
            visible: item.visible !== false,
            ratingItemDescription,
            value,
            ratingGroupDescription,
            parentRatingItemDescription
        };
    }

    private getRenderedItemLabel(itemId: string): string {
        if (!itemId) {
            return "";
        }

        const item = this.util.get(itemId);
        return item?.label || "";
    }

    private extractSummaryTableCells(value: any): string[] {
        if (typeof value !== "string" || value.indexOf("<td") < 0) {
            return [];
        }

        const cells = typeof document === "undefined"
            ? this.extractSummaryTableCellsWithoutDom(value)
            : this.extractSummaryTableCellsWithDom(value);

        return cells
            .map(cell => cell.replace(/\s+/g, " ").trim())
            .filter(cell => !!cell && cell.toLowerCase() !== "undefined");
    }

    private extractSummaryTableCellsWithDom(value: string): string[] {
        const template = document.createElement("template");
        template.innerHTML = value;
        return Array.from(template.content.querySelectorAll("td"))
            .map(cell => cell.textContent || "");
    }

    private extractSummaryTableCellsWithoutDom(value: string): string[] {
        const cells: string[] = [];
        const lowerValue = value.toLowerCase();
        let searchIndex = 0;

        while (searchIndex < lowerValue.length) {
            const startTagIndex = lowerValue.indexOf("<td", searchIndex);
            if (startTagIndex < 0) {
                break;
            }

            const contentStartIndex = lowerValue.indexOf(">", startTagIndex);
            const contentEndIndex = lowerValue.indexOf("</td>", contentStartIndex + 1);
            if (contentStartIndex < 0 || contentEndIndex < 0) {
                break;
            }

            cells.push(this.decodeHtml(this.stripHtmlTags(value.slice(contentStartIndex + 1, contentEndIndex))));
            searchIndex = contentEndIndex + "</td>".length;
        }

        return cells;
    }

    private stripHtmlTags(value: string): string {
        let result = "";
        let insideTag = false;
        for (const character of value) {
            if (character === "<") {
                insideTag = true;
                continue;
            }
            if (character === ">") {
                insideTag = false;
                continue;
            }
            if (!insideTag) {
                result += character;
            }
        }
        return result;
    }

    private decodeHtml(value: string): string {
        if (typeof document === "undefined") {
            return value
                .replace(/&nbsp;/g, " ")
                .replace(/&amp;/g, "&")
                .replace(/&lt;/g, "<")
                .replace(/&gt;/g, ">")
                .replace(/&quot;/g, "\"")
                .replace(/&#39;/g, "'");
        }

        const element = document.createElement("textarea");
        element.innerHTML = value;
        return element.value;
    }

    private inferCoverageSummaryLabel(itemId: string): string {
        if (!itemId) {
            return "";
        }

        if (/_EL(_Header|_\d+M)?$/i.test(itemId)) {
            return "Excess Liability Limit";
        }
        if (/_UM(_Header|_\d+M)?$/i.test(itemId)) {
            return "Optional Excess Uninsured/Underinsured Motorist Limit";
        }
        if (/_EP(LI)?(_Header|_\d+K|_Decline)?$/i.test(itemId)) {
            return "Optional Employment Practices Liability Insurance";
        }
        if (/_PR_(Parent|Yes|No)$/i.test(itemId)) {
            return "Complimentary Insurance Review";
        }

        return "";
    }

    private inferCoverageSummaryValue(itemId: string, tableCells: string[], rawDisplay: string): string {
        const selectedText = tableCells.length > 0 ? tableCells[0] : rawDisplay;

        if (/_EL_/i.test(itemId) || /_UM_/i.test(itemId)) {
            return this.formatMillionLimit(itemId) || this.formatMillionLimit(selectedText) || selectedText || "";
        }

        if (/_EP(LI)?_/i.test(itemId)) {
            if (/decline/i.test(itemId)) {
                return "Declined";
            }

            const amounts = this.extractCurrencyAmounts(selectedText || "");
            const coverage = amounts.length >= 2
                ? `${amounts[0]}/${amounts[1]}`
                : selectedText;

            return coverage
                ? `Employment Practices Liability for domestic employees ${coverage}`
                : "";
        }

        if (/_PR_(Yes|No)$/i.test(itemId)) {
            if (tableCells.length > 0) {
                return tableCells[0];
            }
            return /_Yes$/i.test(itemId)
                ? "Yes, I would like to have the private client team review my personal insurance program."
                : "No, I do not want a review of my personal insurance program at this time.";
        }

        return tableCells.length > 0 ? tableCells.join(" ") : "";
    }

    private formatMillionLimit(value: string): string {
        if (!value) {
            return "";
        }

        const millionSuffixRegex = /_(\d+)M\b/i;
        const suffixMatch = millionSuffixRegex.exec(value);
        if (suffixMatch) {
            return `$${suffixMatch[1]} Million`;
        }

        const currencyRegex = /\$([\d,]+)/;
        const currencyMatch = currencyRegex.exec(value);
        if (!currencyMatch) {
            return "";
        }

        const amount = Number(currencyMatch[1].replace(/,/g, ""));
        if (!amount || amount % 1000000 !== 0) {
            return "";
        }

        return `$${amount / 1000000} Million`;
    }

    private extractCurrencyAmounts(value: string): string[] {
        const amounts: string[] = [];
        const currencyAmountRegex = /\$[\d,]+/g;
        let match = currencyAmountRegex.exec(value);
        while (match) {
            amounts.push(match[0]);
            match = currencyAmountRegex.exec(value);
        }
        return amounts;
    }

    async postAdditionalInfo(): Promise<any> {
        const { applicationName, programName } = this.util.store;
        return this.api("additionalInfo", "post", "/api/intake/processmorecontactinfo", { params: { applicationName, programName } });
    }

    /* ----- Payment ----- */

    async getIsAppliedPayEnabled(): Promise<any> {
        const programName = this.util.get("programName");
        return this.api("appliedPayEnabled", "get", `/api/system/isFeatureFlagEnabled/AppliedPay/${programName}`, { hideError: true });
    }

    async getIsAppliedPayReceiptNumberEnabled(): Promise<boolean> {
        // Only a literal JSON true counts: httpPromise resolves on HTTP 4xx/5xx with the parsed body or the raw text.
        const enabled = await this.api("appliedPayReceiptNumberEnabled", "get", "/api/system/isFeatureFlagEnabled/AppliedPayReceiptNumber", { hideError: true })
            .catch(() => false);
        return enabled === true;
    }

    async postAppliedPayInitiate(): Promise<any> {
        return this.api("appliedPayInitiate", "post", "/api/v2/payments/appliedpay/initiate", { hideError: true });
    }

    async getV2Submission(submissionExternalId: string): Promise<any> {
        if (!submissionExternalId) {
            return null;
        }
        return this.api("v2Submission", "get", `/api/v2/submissions/${encodeURIComponent(submissionExternalId)}`);
    }

    async getV2ParticipantRating(participantRatingId: number | string): Promise<any> {
        if (!participantRatingId) {
            return null;
        }
        return this.api("v2ParticipantRating", "get", `/api/v2/participants/ratings/${encodeURIComponent(String(participantRatingId))}`);
    }

    async getCapabilityContext(): Promise<any> {
        return this.api("capabilityContext", "get", "/api/v2/capabilities/context");
    }

    async exchangeCapabilityLink(subjectToken: string, sessionId?: string): Promise<any> {
        const body = new URLSearchParams();
        body.set("grant_type", TOKEN_EXCHANGE_GRANT);
        body.set("subject_token", subjectToken);
        body.set("subject_token_type", "urn:arc:params:oauth:token-type:email-link");
        body.set("resource", TOKEN_EXCHANGE_RESOURCE);
        if (sessionId) {
            body.set("session_id", sessionId);
        }
        const response = await httpPromise(HTTP_POST_METHOD, TOKEN_EXCHANGE_URL, {
            data: body.toString(),
            contentType: URL_ENCODED_FORM_CONTENT_TYPE
        });
        if (response?.access_token) {
            this.storeCapability(response.access_token, Date.now() + Number(response.expires_in || 0) * 1000);
        }
        return response;
    }

    async exchangeLegacyParticipantRatingLink(subjectToken: string, operation: string, sessionId?: string): Promise<any> {
        const body = new URLSearchParams();
        body.set("grant_type", TOKEN_EXCHANGE_GRANT);
        body.set("subject_token", subjectToken);
        body.set("subject_token_type", "urn:arc:params:oauth:token-type:legacy-participant-rating");
        body.set("resource", TOKEN_EXCHANGE_RESOURCE);
        body.set("operation", operation);
        if (sessionId) {
            body.set("session_id", sessionId);
        }
        const response = await httpPromise(HTTP_POST_METHOD, TOKEN_EXCHANGE_URL, {
            data: body.toString(),
            contentType: URL_ENCODED_FORM_CONTENT_TYPE
        });
        if (response?.access_token) {
            this.storeCapability(response.access_token, Date.now() + Number(response.expires_in || 0) * 1000);
        }
        return response;
    }

    async exchangeLegacySubmissionLink(subjectToken: string, operation: string, sessionId?: string): Promise<any> {
        const body = new URLSearchParams();
        body.set("grant_type", TOKEN_EXCHANGE_GRANT);
        body.set("subject_token", subjectToken);
        body.set("subject_token_type", "urn:arc:params:oauth:token-type:legacy-submission");
        body.set("resource", TOKEN_EXCHANGE_RESOURCE);
        body.set("operation", operation);
        if (sessionId) {
            body.set("session_id", sessionId);
        }
        const response = await httpPromise(HTTP_POST_METHOD, TOKEN_EXCHANGE_URL, {
            data: body.toString(),
            contentType: URL_ENCODED_FORM_CONTENT_TYPE
        });
        if (response?.access_token) {
            this.storeCapability(response.access_token, Date.now() + Number(response.expires_in || 0) * 1000);
        }
        return response;
    }

    async putSaveQuote(): Promise<any> {
        if (this.usesV2ReturningWorkflow()) {
            return this.postV2Intake();
        }
        return this.api("saveQuote", "put", "/api/intake");
    }

    async postPremiumFinance(): Promise<any> {
        return this.api("premiumFinance", "post", "/api/premiumfinance/importquoteasync");
    }

    async putParticipantEnrollment(): Promise<any> {
        if (this.usesV2WebIntake()) {
            return this.postV2Intake();
        }
        if (this.usesV2ReturningWorkflow()) {
            return this.postV2Intake();
        }
        return this.api("participantEnrollment", "put", "/api/intake");
    }

    private usesV2ReturningWorkflow(): boolean {
        const operation = this.util.get(CAPABILITY_CONTEXT_OPERATION_PATH);
        return !!sessionStorage.getItem("arc_capability_access_token") &&
            ["quote:resume", "renewal:resume", "referral:resume", "signature:complete"].indexOf(operation) >= 0;
    }

    private async postV2Intake(): Promise<any> {
        await this.ensureCapability();
        const requestModel = this.model.participantEnrollmentRequest;
        const enrollmentRequest = requestModel ? this.util.get(requestModel) : null;
        // Returning-user capability workflows must use the server-authoritative
        // binding returned by /api/v2/capabilities/context. Do not fall back to
        // a client-supplied participantRatingId for an anonymous request.
        // Applied Pay link creation is a fresh attempt each time. Use rollout
        // to identify that provider and omit any rating id carried by a prior
        // attempt; Card Connect continues using its existing retry binding.
        const isAppliedPayIntake = this.isAppliedPayProgramForUi() && this.util.get("appliedPayActive") === true;
        const participantRatingId = isAppliedPayIntake
            ? undefined
            : this.usesV2ReturningWorkflow()
                ? this.util.get(CAPABILITY_CONTEXT_PARTICIPANT_RATING_ID_PATH)
                : this.util.get("participantRatingId");
        if (!enrollmentRequest) {
            return null;
        }

        const requestIds = this.requestIds("web-intake");
        const payment = this.buildV2Payment(participantRatingId);
        return this.sendV2Intake(enrollmentRequest, participantRatingId, payment, requestIds);
    }

    private buildV2Payment(participantRatingId: number | string): any {
        if (this.util.get("appliedPayActive")) {
            return {
                ParticipantRatingId: participantRatingId ? Number(participantRatingId) : 0,
                Amount: Number(this.util.get("total") || 0),
                PaymentMethod: "",
                PaymentTypeId: 0,
                Notify: true,
                ParticipantRatingStatusId: this.util.get("participantRatingStatusId") || undefined,
                CaptchaToken: this.util.get("captchaToken") || ""
            };
        }

        const paymentMethod = this.getPaymentMethod(this.util.get("paymentType.value"));
        if (!paymentMethod) {
            return null;
        }

        const amount = Number(this.util.get("total") || 0);
        const rawPaymentRequest = this.util.get(this.model.paymentRequest) || {};
        const paymentRequest = paymentMethod === "ACH"
            ? this.mapAchPaymentRequest(rawPaymentRequest, participantRatingId, amount)
            : rawPaymentRequest;
        return {
            ParticipantRatingId: participantRatingId ? Number(participantRatingId) : 0,
            Amount: amount,
            PaymentMethod: paymentMethod,
            PaymentTypeId: paymentMethod === "CREDITCARD" ? 2 : 1,
            Notify: true,
            ParticipantRatingStatusId: this.util.get("participantRatingStatusId") || undefined,
            CaptchaToken: this.util.get("captchaToken") || "",
            PaymentRequest: paymentRequest
        };
    }

    private getPaymentMethod(selectedPaymentType: string): string {
        if (selectedPaymentType === "creditcard") {
            return "CREDITCARD";
        }
        if (selectedPaymentType === "ach") {
            return "ACH";
        }
        return "";
    }

    private mapAchPaymentRequest(rawPaymentRequest: any, participantRatingId: number | string, amount: number): any {
        // Match App UI's PaymentWrapper mapping for ACH values stored by the form.
        return {
            ...rawPaymentRequest,
            ParticipantRatingId: participantRatingId ? Number(participantRatingId) : 0,
            PaymentAmount: amount,
            PaymentMethod: undefined,
            BankAccountOwner: rawPaymentRequest.BankAccountOwner || rawPaymentRequest.bankAccountOwner || rawPaymentRequest.accountOwnership,
            AccountNumber: rawPaymentRequest.AccountNumber || rawPaymentRequest.accountNumber,
            BankAccountNumber: rawPaymentRequest.BankAccountNumber || rawPaymentRequest.bankAccountNumber || rawPaymentRequest.accountNumber,
            RoutingNumber: rawPaymentRequest.RoutingNumber || rawPaymentRequest.routingNumber,
            IsChecking: this.isCheckingAccount(rawPaymentRequest),
            IsPersonalAccount: this.isPersonalAccount(rawPaymentRequest)
        };
    }

    private isCheckingAccount(paymentRequest: any): boolean {
        if (paymentRequest.IsChecking != null) {
            return paymentRequest.IsChecking;
        }
        if (paymentRequest.isChecking != null) {
            return paymentRequest.isChecking;
        }
        return paymentRequest.accountType === "Checking";
    }

    private isPersonalAccount(paymentRequest: any): boolean {
        if (paymentRequest.IsPersonalAccount != null) {
            return paymentRequest.IsPersonalAccount;
        }
        if (paymentRequest.isPersonalAccount != null) {
            return paymentRequest.isPersonalAccount;
        }
        return paymentRequest.accountOwnership === "Personal";
    }

    private async sendV2Intake(
        enrollmentRequest: any,
        participantRatingId: number | string,
        payment: any,
        requestIds: { [key: string]: string }): Promise<any> {
        const capabilityToken = this.getIntakeSubmitCapabilityToken(participantRatingId);
        if (Number(participantRatingId) > 0 && !capabilityToken) {
            this.clearIntakeUpdateCapability();
            this.dispatchRatingBoundRecoveryRequired(Number(participantRatingId), enrollmentRequest?.Participant?.Email || enrollmentRequest?.participant?.email);
            throw new Error("Your session expired. Verify your email to continue the existing application.");
        }
        try {
            const response = await httpPromise("POST", environment.apiBase + "/api/v2/submissions/intake", {
                data: JSON.stringify({ participantRatingId: participantRatingId ? Number(participantRatingId) : null, enrollmentRequest, payment }),
                contentType: "application/json",
                rejectHttpErrors: true,
                headers: {
                    ...(capabilityToken ? { "X-Capability-Token": capabilityToken } : {}),
                    ...requestIds
                }
            });
            if (response) {
                if (this.isAppliedPayPaymentActive() && this.isFailedHttpResponse(response)) {
                    this.discardFailedAppliedPayIntake();
                } else {
                    this.storeIntakeUpdateCapabilityToken(response);
                    this.util.set({
                        participantEnrollmentResponse: response,
                        participantRatingId: response.participantRatingId || participantRatingId
                    }, { batch: true });
                }
            }
            return response;
        } catch (error: any) {
            this.handleCapabilityRevoked(error, Number(participantRatingId), enrollmentRequest?.Participant?.Email || enrollmentRequest?.participant?.email);
            const problem = error?.response || error?.error || error;
            if (this.isAppliedPayPaymentActive() && this.isFailedHttpResponse(problem)) {
                this.discardFailedAppliedPayIntake();
                throw error;
            }

            this.storeIntakeUpdateCapabilityToken(error);
            // Problem-details responses from intake include the rating and
            // submission identifiers created before payment failed. Preserve
            // them so the next submit reuses the authorized existing context
            // instead of creating another submission/rating.
            const failedRatingId = error?.participantRatingId || error?.extensions?.participantRatingId;
            if (failedRatingId) {
                this.util.set({
                    participantRatingId: Number(failedRatingId),
                    participantEnrollmentResponse: error
                }, { batch: true });
            }
            throw error;
        }
    }

    private isAppliedPayPaymentActive(): boolean {
        return this.isAppliedPayProgramForUi() && this.util.get("appliedPayActive") === true;
    }

    private isFailedHttpResponse(response: any): boolean {
        const status = Number(response?.status || response?.response?.status || response?.error?.status);
        return status >= 400 && status < 600;
    }

    private discardFailedAppliedPayIntake(): void {
        // Any failed Applied Pay intake abandons the rating created for that
        // link attempt. The next click must start a fresh intake and link.
        this.clearIntakeUpdateCapability();
        this.util.set({
            participantRatingId: undefined,
            participantEnrollmentResponse: undefined
        }, { batch: true });
    }

    private storeIntakeUpdateCapabilityToken(response: any): void {
        const body = response?.response || response?.error || response;
        const token = body?.intakeCapabilityToken || body?.extensions?.intakeCapabilityToken;
        const participantRatingId = Number(body?.participantRatingId ?? body?.extensions?.participantRatingId);
        const expiryValue = body?.intakeCapabilityExpiresAt || body?.extensions?.intakeCapabilityExpiresAt;
        const expiresAt = typeof expiryValue === "number" ? expiryValue : Date.parse(expiryValue || "");
        if (!token || !Number.isFinite(participantRatingId) || participantRatingId <= 0 ||
            !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
            return;
        }

        sessionStorage.setItem(INTAKE_UPDATE_CAPABILITY_TOKEN_KEY, token);
        sessionStorage.setItem(INTAKE_UPDATE_CAPABILITY_RATING_ID_KEY, String(participantRatingId));
        sessionStorage.setItem(INTAKE_UPDATE_CAPABILITY_EXPIRES_AT_KEY, String(expiresAt));
    }

    private getIntakeSubmitCapabilityToken(participantRatingId: number | string): string | null {
        const requestedRatingId = Number(participantRatingId);
        if (!Number.isFinite(requestedRatingId) || requestedRatingId <= 0) {
            return sessionStorage.getItem(CAPABILITY_ACCESS_TOKEN_KEY);
        }

        const updateToken = sessionStorage.getItem(INTAKE_UPDATE_CAPABILITY_TOKEN_KEY);
        const boundRatingId = Number(sessionStorage.getItem(INTAKE_UPDATE_CAPABILITY_RATING_ID_KEY));
        const expiresAt = Number(sessionStorage.getItem(INTAKE_UPDATE_CAPABILITY_EXPIRES_AT_KEY));
        if (updateToken && boundRatingId === requestedRatingId && Number.isFinite(expiresAt) && expiresAt > Date.now()) {
            return updateToken;
        }

        // Returning email-link capabilities remain valid for their exact
        // context (signature, renewal, quote, referral, etc.). Never pair an
        // unbound typed-email token with a client-selected existing rating.
        const contextRatingId = Number(this.util.get(CAPABILITY_CONTEXT_PARTICIPANT_RATING_ID_PATH));
        const operation = this.util.get(CAPABILITY_CONTEXT_OPERATION_PATH);
        return operation && contextRatingId === requestedRatingId
            ? sessionStorage.getItem(CAPABILITY_ACCESS_TOKEN_KEY)
            : null;
    }

    private clearIntakeUpdateCapability(): void {
        sessionStorage.removeItem(INTAKE_UPDATE_CAPABILITY_TOKEN_KEY);
        sessionStorage.removeItem(INTAKE_UPDATE_CAPABILITY_RATING_ID_KEY);
        sessionStorage.removeItem(INTAKE_UPDATE_CAPABILITY_EXPIRES_AT_KEY);
    }

    private getRatePreviewCapabilityToken(): string | null {
        return this.util.get("participantEnrollmentResponse.intakeCapabilityToken") ||
            sessionStorage.getItem(INTAKE_UPDATE_CAPABILITY_TOKEN_KEY) ||
            sessionStorage.getItem(CAPABILITY_ACCESS_TOKEN_KEY);
    }

    private async ensureCapability(): Promise<void> {
        const token = sessionStorage.getItem("arc_capability_access_token");
        const returningOperation = this.util.get(CAPABILITY_CONTEXT_OPERATION_PATH);
        const program = String(this.util.get("programName") || "").trim().toLowerCase();
        const email = String(this.util.get("email.value") || this.util.get("verifyEmail.value") || "").trim().toLowerCase();
        if (token && returningOperation) {
            if (this.isCapabilityExpiredOrExpiryUnknown()) {
                this.clearCurrentApplicantCapability();
                throw new Error("The capability for this link has expired. Reopen a current link to continue.");
            }
            return;
        }
        if (token &&
            sessionStorage.getItem("arc_capability_program") === program &&
            sessionStorage.getItem("arc_capability_email") === email &&
            !this.isCapabilityExpiredOrExpiryUnknown()) {
            return;
        }

        if (token) {
            this.clearCurrentApplicantCapability();
        }
        const response = await this.issueCapabilityForCurrentApplicant();
        if (!response?.token || this.isCapabilityExpiredOrExpiryUnknown()) {
            this.clearCurrentApplicantCapability();
            throw new Error("A current capability token is required for the Web.Intake V2 workflow.");
        }
    }

    async postPayment(): Promise<any> {
        const paymentType = this.util.get("creditCardNumber.value") ? "creditcard" : "ach";
        return this.api("payment", "post", `/api/payment/${paymentType}`);
    }

    async putPaymentError(): Promise<any> {
        return this.api("paymentError", "post", "/api/postingstatus/status");
    }

    async postPostingStatus(): Promise<any> {
        return this.api("postingStatus", "post", "/api/postingstatus/status");
    }

    async postPaymentReceiptEmail(): Promise<any> {
        return this.api("paymentReceipt", "post", "/api/notification/sendpaymentreceiptemailasync");
    }

    async pingAms() {
        const participantRatingId = this.util.get("participantRatingId");
        return this.api("ams", "get", `/api/ams/async/${participantRatingId}`, { hideError: true });
    }

    async pingPostPayCorrespondence() {
        const participantRatingId = this.util.get("participantRatingId");
        return this.api("postPayCorrespondence", "post", `/api/notification/SendPostPayCorrespondence/${participantRatingId}`);
    }

    /* ----- Middleware ----- */

    // "applicationForm", "GET", "api/intake", { mock: ataStatic }
    async api(key: string, verb: string, url: string, config?: any): Promise<any> {
        try {
            const response = await this.requestHandler(key, verb, url, config);
            if (url.toLowerCase().startsWith("/api/v2/")) { this.handleCapabilityRevoked(response); }
            return this.responseHandler(key, response, config);
        } catch (error: any) {
            if (url.toLowerCase().startsWith("/api/v2/")) { this.handleCapabilityRevoked(error); }
            throw error;
        }
    }

    private handleCapabilityRevoked(error: any, participantRatingId = 0, email = ""): void {
        const problem = error?.response || error?.error || error;
        if (problem?.type === "/problems/capability-revoked") {
            this.clearCurrentApplicantCapability();
            window.dispatchEvent(new Event("arc-capability-revoked"));
            return;
        }
        if (Number(error?.status || problem?.status) === 401 && participantRatingId > 0) {
            this.clearCurrentApplicantCapability();
            this.dispatchRatingBoundRecoveryRequired(participantRatingId, email);
        }
    }

    private dispatchRatingBoundRecoveryRequired(participantRatingId: number, email: string): void {
        window.dispatchEvent(new CustomEvent("arc-rating-bound-recovery-required", {
            detail: { participantRatingId, email: String(email || "") }
        }));
    }

    async requestHandler(key, verb, url, config: any = {}) {
        const { mock, params } = config;
        if (mock) { return mock; }                                                  // Return mock response if provided one.
        const requestModel = this.model[`${key}Request`];                       // Get model for request.
        const data = requestModel ? this.util.get(requestModel) : null;         // Populate request model from store.
        // Do not attach a customer capability to legacy/bootstrap endpoints.
        // Those routes use the authenticated/admin authorization path and can
        // incorrectly interpret a typed-email capability as requiring an
        // admin program-enrollment scope. V2 customer calls add the header at
        // their explicit call sites.
        const capabilityToken = sessionStorage.getItem("arc_capability_access_token");
        const headers = capabilityToken && url.toLowerCase().startsWith("/api/v2/")
            ? { "X-Capability-Token": capabilityToken }
            : undefined;
        return httpPromise(verb, environment.apiBase + url, { data, params, headers });  // Get response from API.
    }

    responseHandler(key, response, config: any = {}) {
        // Store response.
        this.util.set({ [`${key}Response`]: response }, { batch: true });   // Set response in store.

        // Store properties from response.
        const propsModel = this.model[`${key}Props`];                                   // Get model for properties.
        if (propsModel) { this.util.set(this.util.get(propsModel), { batch: true }); }      // Populate properties in store using props model.

        // Check for errors.
        const showErrors = !environment.production || !config.hideError;    // Show errors in prod if not given hideError property.
        if (showErrors) {
            const errors = this.errors.getErrors(`${key}Response`, response);               // Get errors.
            if (errors.length) {
                if (this.errors[`${key}Error`]) { return this.errors[`${key}Error`](errors); }  // Use specific error if it exists.
                return this.errors.serverError(errors);                                     // Otherwise, display generic server error.
            }
        }

        // Return response.
        return response;
    }

}
